import { ReadonlyVec3 } from 'gl-matrix';
import { Trash2 } from 'lucide-react';
import React, { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { PointMm } from '~/domain/geometry/patientGeometry';
import {
    canvasMidpoint,
    type CanvasPoint,
    patientToCanvasPoint,
} from '~/engine/view/planeViewGeometry';
import type { CanvasFrameGeometry } from '~/engine/view/TextureView';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import { useRemoveAnnotation } from '~/hooks/useRemoveAnnotation';
import {
    AnnotationInteraction,
    PlacedAnnotation,
    useAnnotationPointer,
} from '~/hooks/viewer/annotationOverlay';
import { MeasureMode, measureModeColor } from '~/utils/annotationKinds';

/** Opacity for annotations placed on a slice other than the one shown. */
export const OFF_PLANE_OPACITY = 0.4;

/** Dash of a shape drawn off its slice, and of a shape being placed. */
export const DASHED = '5 4';

export const SHAPE_STROKE_PX = 1.75;

/** An invisible band along a stroke, wider than it so it is easy to land on, that takes the pointer. */
export const HIT_BAND = {
    'data-testid': 'annotation-hit-band',
    fill: 'none',
    stroke: 'transparent',
    strokeWidth: 14,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    style: { pointerEvents: 'stroke', cursor: 'pointer' },
} as const;

/** Unit vector from a to b in canvas space, or null if coincident. */
export function unitTo(a: CanvasPoint, b: CanvasPoint): CanvasPoint | null {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const length = Math.hypot(dx, dy);
    if (length === 0) {
        return null;
    }
    return { x: dx / length, y: dy / length };
}

/** Label position for a distance segment: nudged off the line midpoint. */
export function distanceLabelPos(a: CanvasPoint, b: CanvasPoint): CanvasPoint {
    const mid = canvasMidpoint(a, b);
    const dir = unitTo(a, b);
    if (!dir) {
        return { x: mid.x + 8, y: mid.y - 8 };
    }
    // Perpendicular offset so the text doesn't sit on the line.
    return { x: mid.x - dir.y * 12 + 6, y: mid.y + dir.x * 12 - 6 };
}

const PILL_PAD_X = 5;
const PILL_HEIGHT_PX = 18;
/** Labels are short fixed-format values, so a per-character estimate sizes the pill. */
const CHAR_WIDTH_PX = 7.5;

/** The bin a pointed-at label shows at its end, px. */
const LABEL_BIN_PX = 13;

/**
 * A pill starting at its anchor and centred on it vertically, or centred
 * above it. One with a bin is wider to the right, so its text stays put.
 */
function pillBox(
    at: CanvasPoint,
    text: string,
    above = false,
    withBin = false
) {
    const width = text.length * CHAR_WIDTH_PX + PILL_PAD_X * 2;
    return {
        x: above ? at.x - width / 2 : at.x,
        y: above ? at.y - PILL_HEIGHT_PX : at.y - PILL_HEIGHT_PX / 2,
        width: width + (withBin ? LABEL_BIN_PX + PILL_PAD_X : 0),
    };
}

/** The space kept between two labels moved apart, px. */
const LABEL_GAP_PX = 6;

/**
 * Where a label of `text` anchored at `at` goes so its pill keeps clear of
 * the label `other`: moved down past it when the two would overlap.
 */
export function labelClearOf(
    at: CanvasPoint,
    text: string,
    other: { readonly at: CanvasPoint; readonly text: string }
): CanvasPoint {
    const box = pillBox(at, text);
    const otherBox = pillBox(other.at, other.text);
    const clearance = PILL_HEIGHT_PX + LABEL_GAP_PX;
    const overlapsX =
        box.x < otherBox.x + otherBox.width && otherBox.x < box.x + box.width;
    return overlapsX && Math.abs(at.y - other.at.y) < clearance
        ? { x: at.x, y: other.at.y + clearance }
        : at;
}

/**
 * A value on a dark pill, legible over bright and dark anatomy alike.
 * Fixed-width digits stop a value changing during a drag from jumping sideways.
 * With `onDelete`, a bin in the value's colour at the pill's end calls it.
 */
export const ValueLabel = (props: {
    at: CanvasPoint;
    text: string;
    color: string;
    above?: boolean;
    onDelete?: () => void;
}) => {
    const { onDelete } = props;
    const { x, y, width } = pillBox(
        props.at,
        props.text,
        props.above,
        !!onDelete
    );
    const binX = x + width - PILL_PAD_X - LABEL_BIN_PX;
    return (
        <g style={{ userSelect: 'none' }}>
            <rect
                x={x}
                y={y}
                width={width}
                height={PILL_HEIGHT_PX}
                rx={3}
                fill="rgba(0, 0, 0, 0.6)"
            />
            <text
                x={x + PILL_PAD_X}
                y={y + PILL_HEIGHT_PX / 2}
                fill={props.color}
                fontSize={13}
                fontFamily="sans-serif"
                fontWeight={600}
                dominantBaseline="central"
                style={{ fontVariantNumeric: 'tabular-nums' }}
            >
                {props.text}
            </text>
            {onDelete && (
                <FormattedMessage id="measure.delete">
                    {(name) => (
                        <g
                            role="button"
                            aria-label={name.join('')}
                            style={{ cursor: 'pointer' }}
                            onMouseDown={(event) => {
                                // Deletes only: no selection, placement or pan.
                                event.stopPropagation();
                                event.preventDefault();
                            }}
                            onClick={onDelete}
                        >
                            <rect
                                x={binX - 2}
                                y={y}
                                width={LABEL_BIN_PX + 4}
                                height={PILL_HEIGHT_PX}
                                fill="transparent"
                            />
                            <Trash2
                                x={binX}
                                y={y + (PILL_HEIGHT_PX - LABEL_BIN_PX) / 2}
                                size={LABEL_BIN_PX}
                                color={props.color}
                            />
                        </g>
                    )}
                </FormattedMessage>
            )}
        </g>
    );
};

/** The canvas an annotation is drawn over. */
export interface CanvasProjection {
    /** The X axis of the camera the canvas shows, patient space. */
    readonly viewXDirection: ReadonlyVec3;
    /** A pane point on the canvas, through the camera of the frame it shows. */
    readonly toCanvas: (pointMm: PointMm) => CanvasPoint;
}

/** The canvas of `view`, showing `frame`, or the live camera without one. */
export function canvasProjection(
    view: PlaneView,
    frame?: CanvasFrameGeometry
): CanvasProjection {
    return {
        viewXDirection: frame?.viewXDirection ?? view.viewXDirection,
        toCanvas: (pointMm) => patientToCanvasPoint(pointMm, view, frame),
    };
}

export interface PlacedAnnotationProps {
    readonly id: string;
    readonly view: PlaneView;
    /** The frame published on the view's canvas; absent, the live camera. */
    readonly frame?: CanvasFrameGeometry;
    readonly interaction: AnnotationInteraction;
    /** Above every band, so a handle on another shape's stroke can be grabbed. */
    readonly handleLayer: SVGGElement | null;
    readonly labelLayer: SVGGElement | null;
    /** Changes whenever the view's projection may have. */
    readonly redrawTick: number;
}

/**
 * Dashed off its slice, so a shape never reads as lying on the slice shown,
 * and faint there unless emphasised.
 */
export function placedShapeStyle(placed: PlacedAnnotation) {
    const { onCurrentSlice, emphasised } = placed;
    return {
        opacity: onCurrentSlice || emphasised ? 1 : OFF_PLANE_OPACITY,
        strokeWidth: SHAPE_STROKE_PX,
        strokeDasharray: onCurrentSlice ? undefined : DASHED,
    };
}

/** The cursor over an edit handle. */
export type HandleCursor = 'move' | 'ew-resize' | 'nwse-resize' | 'nesw-resize';

interface PlacedShapeProps extends PlacedAnnotationProps {
    readonly placed: PlacedAnnotation;
    readonly mode: MeasureMode;
    readonly label: string;
    readonly labelAt: CanvasPoint;
    readonly labelAbove?: boolean;
    readonly bands: ReactNode;
    /** Where the edit handles sit; a press on one starts `beginDrag` with its index. */
    readonly handles: readonly CanvasPoint[];
    readonly handleCursors?: readonly HandleCursor[];
    readonly beginDrag: (event: React.MouseEvent, handle: number) => void;
    readonly children: ReactNode;
}

/**
 * A placed shape with the bands, edit handles and label `placed` offers, the
 * handles and the label on their layers. Bands and handles share one pointer,
 * so moving from a band onto a handle keeps the hover. A pointed-at label
 * shows, at its end, a bin that deletes the shape.
 */
export function PlacedShape(props: PlacedShapeProps) {
    const { id, placed, mode, label, handleLayer, labelLayer } = props;
    const { pointer } = placed;
    const remove = useRemoveAnnotation();
    const [labelHovered, labelPointer] = useAnnotationPointer(id, placed.label);
    const pill = (
        <g
            data-testid="annotation-label"
            data-annotation-id={id}
            data-kind={mode}
            data-selected={placed.selected}
            opacity={placedShapeStyle(placed).opacity}
            style={{
                pointerEvents: placed.label ? 'all' : 'none',
                cursor: placed.label ? 'pointer' : undefined,
            }}
            {...labelPointer}
        >
            <ValueLabel
                at={props.labelAt}
                text={label}
                color={measureModeColor(mode)}
                above={props.labelAbove}
                onDelete={labelHovered ? () => remove(id) : undefined}
            />
        </g>
    );
    return (
        <>
            {props.children}
            {placed.bands && <g {...pointer}>{props.bands}</g>}
            {placed.handles &&
                handleLayer &&
                createPortal(
                    <g {...pointer}>
                        {props.handles.map((at, handle) => (
                            <circle
                                key={handle}
                                data-testid="annotation-handle"
                                cx={at.x}
                                cy={at.y}
                                r={6}
                                fill="rgba(0, 0, 0, 0.35)"
                                stroke="#fff"
                                strokeWidth={1.5}
                                style={{
                                    pointerEvents: 'all',
                                    cursor:
                                        props.handleCursors?.[handle] ?? 'move',
                                }}
                                onMouseDown={(event) =>
                                    pointer.onMouseDown(event, (press) =>
                                        props.beginDrag(press, handle)
                                    )
                                }
                            />
                        ))}
                    </g>,
                    handleLayer
                )}
            {labelLayer && createPortal(pill, labelLayer)}
        </>
    );
}

/**
 * The full-viewport SVG layer the annotation overlays draw into. Click-through
 * by design: only the elements that opt back in (hit bands, handles, labels)
 * intercept the engine tool's mouse events.
 */
export default function AnnotationOverlaySvg({
    children,
}: {
    children: ReactNode;
}) {
    return (
        <svg
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 6,
            }}
        >
            {children}
        </svg>
    );
}
